import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Icon } from '@iconify/react'
import { useI18n, languages } from '../lib/i18n'

interface LanguageSwitcherProps {
  variant?: 'desktop' | 'mobile'
}

export default function LanguageSwitcher({ variant = 'desktop' }: LanguageSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const { lang, setLang, t } = useI18n()

  const currentLang = languages.find(l => l.code === lang) || languages[0]

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const isMobile = variant === 'mobile'

  return (
    <div ref={dropdownRef} className="relative">
      {/* Trigger Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center gap-2
          ${isMobile
            ? 'bg-gray-100 hover:bg-gray-200 px-2 py-1.5 rounded-lg'
            : 'bg-[#F4F5F7] hover:bg-gray-200 px-3 py-2 rounded-xl border border-gray-200'
          }
          transition-all duration-200
        `}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <Icon
          icon="ph:globe"
          className={`text-[#172B4D] ${isMobile ? 'w-4 h-4' : 'w-5 h-5'}`}
        />
        <span className={`font-medium text-[#172B4D] ${isMobile ? 'text-xs' : 'text-sm'}`}>
          {currentLang.code.toUpperCase()}
        </span>
        <Icon
          icon="ph:caret-down-bold"
          className={`text-gray-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''} ${isMobile ? 'w-3 h-3' : 'w-4 h-4'}`}
        />
      </motion.button>

      {/* Dropdown Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className={`
              absolute top-full mt-2
              ${isMobile ? 'right-0 w-40' : 'right-0 w-48'}
              bg-white rounded-xl shadow-xl border border-gray-100
              overflow-hidden z-[100]
            `}
          >
            {/* Header */}
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                {t.nav.selectLanguage}
              </p>
            </div>

            {/* Language Options */}
            <div className="max-h-64 overflow-y-auto py-1">
              {languages.map((langOption) => (
                <motion.button
                  key={langOption.code}
                  onClick={() => {
                    setLang(langOption.code)
                    setIsOpen(false)
                  }}
                  className={`
                    w-full flex items-center gap-3 px-4 py-2.5
                    hover:bg-[#0052CC]/5 transition-colors
                    ${lang === langOption.code ? 'bg-[#0052CC]/10' : ''}
                  `}
                  whileHover={{ x: 4 }}
                >
                  <span className="text-xl">{langOption.flag}</span>
                  <span className={`
                    text-sm font-medium
                    ${lang === langOption.code ? 'text-[#0052CC]' : 'text-[#172B4D]'}
                  `}>
                    {langOption.name}
                  </span>
                  {lang === langOption.code && (
                    <Icon
                      icon="ph:check-bold"
                      className="ml-auto text-[#0052CC] w-4 h-4"
                    />
                  )}
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
